// hooks/useTouchHandler.js
import { useRef, useCallback } from 'react';

export function useTouchHandler(onClose, opts = {}) {
  const threshold = opts.threshold ?? 70;      // px pulled down before closing
  const maxAngle = opts.maxAngle ?? 25;        // degrees; keep gesture mostly vertical
  const minTravel = opts.minTravel ?? 8;       // ignore micro-movements
  const minVelocity = opts.minVelocity ?? 0.25;// px/ms for quick flicks

  const startX = useRef(0);
  const startY = useRef(0);
  const lastY  = useRef(0);
  const startT = useRef(0);
  const dragging = useRef(false);
  const consumed = useRef(false);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    lastY.current  = t.clientY;
    startT.current = performance.now();
    dragging.current = true;
    consumed.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!dragging.current) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;

    // Ignore tiny moves (taps)
    if (Math.abs(dy) < minTravel && Math.abs(dx) < minTravel) return;

    // Only react to mostly-vertical, downward drags
    const angle = Math.atan2(Math.abs(dy), Math.abs(dx)) * (180 / Math.PI);
    if (dy <= 0 || angle < (90 - maxAngle)) return;

    // Prevent page scroll while dragging the sheet
    e.preventDefault();
    e.stopPropagation();
    consumed.current = true;
    lastY.current = t.clientY;
  }, [maxAngle, minTravel]);

  const handleTouchEnd = useCallback((e) => {
    if (!dragging.current) return;
    dragging.current = false;

    const dy = lastY.current - startY.current;
    const dt = Math.max(1, performance.now() - startT.current);
    const v  = dy / dt; // px/ms

    // Close only if pulled far enough or flicked fast enough
    if (dy >= threshold || v >= minVelocity) {
      onClose?.();
    } else {
      if (consumed.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, [threshold, minVelocity, onClose]);

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
} 